import type {
  DashboardOverview, ResidencyRow, ComplianceCard, PiaItem, PlanInfo,
} from './client';

/**
 * Demo / Mock data used to drive the UI without backend dependency.
 * Enable via ?mock=1 query or VITE_USE_MOCK=true.
 */
export const mockOverview: DashboardOverview = {
  securityScore: 94,
  scoreTrendDays30: 6,
  vulnerabilities: [
    { severity: 'High',     finding: 'Open SSH port to internet (port 22)', assetName: 'vm-prod-ai-01', status: 'InProgress' },
    { severity: 'High',     finding: 'Public read access on PII container', assetName: 'stcontosopii',  status: 'InProgress' },
    { severity: 'Medium',   finding: 'TLS 1.0 enabled on storage endpoint', assetName: 'stcontosoai',   status: 'InProgress' },
    { severity: 'Medium',   finding: 'Service account lacks MFA',           assetName: 'sp-deploy-bot', status: 'InProgress' },
    { severity: 'Medium',   finding: 'Storage firewall allows all networks', assetName: 'stmodelstore', status: 'Resolved' },
    { severity: 'Medium',   finding: 'Unencrypted model artifact bucket',   assetName: 'ml-registry',   status: 'Resolved' },
    { severity: 'Medium',   finding: 'Audit retention below baseline',      assetName: 'log-prod-eu',   status: 'Resolved' },
    { severity: 'Medium',   finding: 'Missing private endpoint on API',     assetName: 'api-prod-west', status: 'Resolved' },
    { severity: 'Low',      finding: 'Diagnostic log retention < 90 days',  assetName: 'log-prod-eu',   status: 'Resolved' },
    { severity: 'Low',      finding: 'Unused reader group assigned',        assetName: 'entra-readers', status: 'Resolved' },
    { severity: 'Informational', finding: 'Monthly baseline evidence ready', assetName: 'soc2-evidence', status: 'Resolved' },
  ],
  rbac: {
    totalUsers: 256,
    roles: 128,
    mfaCoveragePct: 98,
    criticalAccessViolations: 0,
  },
  globalCompliancePct: 98.6,
};

export const mockResidency: ResidencyRow[] = [
  { country: 'US', countryName: 'United States',  isCompliant: true,  assetCount: 145 },
  { country: 'CA', countryName: 'Canada',         isCompliant: true,  assetCount: 32 },
  { country: 'GB', countryName: 'United Kingdom', isCompliant: true,  assetCount: 58 },
  { country: 'DE', countryName: 'Germany',        isCompliant: true,  assetCount: 42 },
  { country: 'FR', countryName: 'France',         isCompliant: true,  assetCount: 28 },
  { country: 'JP', countryName: 'Japan',          isCompliant: true,  assetCount: 38 },
  { country: 'KR', countryName: 'South Korea',    isCompliant: true,  assetCount: 22 },
  { country: 'AU', countryName: 'Australia',      isCompliant: true,  assetCount: 19 },
  { country: 'CN', countryName: 'China',          isCompliant: true,  assetCount: 24 },
  { country: 'IN', countryName: 'India',          isCompliant: true,  assetCount: 18 },
  { country: 'BR', countryName: 'Brazil',         isCompliant: false, assetCount: 6, note: 'LGPD residency check pending' },
  { country: 'RU', countryName: 'Russia',         isCompliant: false, assetCount: 2, note: 'Sanctions review' },
  { country: 'ZA', countryName: 'South Africa',   isCompliant: true,  assetCount: 4 },
  { country: 'MX', countryName: 'Mexico',         isCompliant: true,  assetCount: 11 },
];

export const mockCompliance: ComplianceCard[] = [
  { frameworkCode: 'SOC2',     frameworkName: 'SOC 2 Type II',       description: 'Service Organization Controls', lastGeneratedAt: '2026-04-15', status: 'Ready', openViolations: 0, downloadUrl: '#' },
  { frameworkCode: 'ISO27001', frameworkName: 'ISO/IEC 27001:2022',  description: 'Information security mgmt',     lastGeneratedAt: '2026-03-30', status: 'Ready', openViolations: 1, downloadUrl: '#' },
  { frameworkCode: 'GDPR',     frameworkName: 'GDPR',                description: 'EU data protection',            lastGeneratedAt: '2026-05-01', status: 'Ready', openViolations: 0, downloadUrl: '#' },
  { frameworkCode: 'HIPAA',    frameworkName: 'HIPAA / HITRUST',     description: 'US healthcare data',            lastGeneratedAt: '2026-02-22', status: 'Ready', openViolations: 2, downloadUrl: '#' },
  { frameworkCode: 'ISO27017', frameworkName: 'ISO/IEC 27017',       description: 'Cloud security controls',       lastGeneratedAt: '2026-04-10', status: 'Ready', openViolations: 0, downloadUrl: '#' },
  { frameworkCode: 'ISO27018', frameworkName: 'ISO/IEC 27018',       description: 'Cloud PII protection',          lastGeneratedAt: '2026-04-10', status: 'Ready', openViolations: 0, downloadUrl: '#' },
];

export const mockPia: PiaItem[] = [
  { id: '1', modelOrFeatureName: 'Customer Support Copilot',    status: 'Completed', riskScore: 18, reviewer: { reviewerName: 'Alicia Morgan',    reviewerEmail: 'alicia@contoso.com' }, dueDate: '2025-05-20' },
  { id: '2', modelOrFeatureName: 'AI Document Summarizer',      status: 'Completed', riskScore: 32, reviewer: { reviewerName: 'Daniel Kim',       reviewerEmail: 'daniel@contoso.com' }, dueDate: '2025-05-28' },
  { id: '3', modelOrFeatureName: 'Predictive Analytics Engine', status: 'InReview',  riskScore: 56, reviewer: { reviewerName: 'Ravi Singh',       reviewerEmail: 'ravi@contoso.com'   }, dueDate: '2025-06-10' },
  { id: '4', modelOrFeatureName: 'HR Resume Screening',         status: 'Planned',   riskScore: 72, reviewer: { reviewerName: 'Linda Chen',       reviewerEmail: 'linda@contoso.com'  }, dueDate: '2025-06-25' },
  { id: '5', modelOrFeatureName: 'AI Code Assistant',           status: 'Planned',   riskScore: 42, reviewer: { reviewerName: 'Michael Johnson',  reviewerEmail: 'michael@contoso.com'}, dueDate: '2025-07-05' },
];

export const mockPlan: PlanInfo = {
  name: 'Contoso Corp',
  planId: 'premium-user',
  displayName: 'Premium',
  maxGpuCount: 32,
  seats: 25,
  monthlyPricePerSeat: 5000,
  allFrameworks: true,
  status: 'Active',
};

export const useMock = () => {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('mock') === '1'
      || import.meta.env.VITE_USE_MOCK === 'true';
};
